import { useEffect, useRef, useState } from "react";

import type { ApiTypes } from "@fluid-app/fluid";
import { getSettings } from "@fluid-app/fluid";

import { waitForSdkReady } from "./sdkInitialization";

// Use type from the API client instead of defining our own
type FluidSettings = ApiTypes["settings-retrieve"]["response"];

/**
 * Custom hook for accessing Fluid settings in widgets
 *
 * @returns The settings object and utility functions
 */
export function useFluidSettings() {
  const [settings, setSettings] = useState<FluidSettings | null>(null);
  const settingsInitialized = useRef(false);

  // Set up event listener for settings availability
  useEffect(() => {
    // Skip this effect entirely during server-side rendering
    if (typeof window === "undefined") {
      return;
    }

    const handleSettingsAvailable = () => {
      const settingsData = getSettings();
      if (settingsData) {
        setSettings(settingsData);
      }
    };

    // Set up event listener
    const initialize = async () => {
      try {
        if (settingsInitialized.current) return;
        settingsInitialized.current = true;

        await waitForSdkReady();

        // Check if settings already exist
        const existingSettings = getSettings();
        if (existingSettings) {
          setSettings(existingSettings);
        }

        // Add event listener for future settings updates
        window.addEventListener("SETTINGS_AVAILABLE", handleSettingsAvailable);
      } catch (error) {
        console.warn("Could not initialize settings:", error);
      }
    };

    void initialize();

    // Clean up event listener
    return () => {
      window.removeEventListener("SETTINGS_AVAILABLE", handleSettingsAvailable);
    };
  }, []);

  // Helper to apply CSS variables to shadow DOM
  const applyCompanyColorToShadowDOM = (
    shadowRoot: ShadowRoot | null,
    colorValue?: string,
  ) => {
    if (shadowRoot && colorValue) {
      // Set company color CSS variable on the shadow root host
      const host = shadowRoot.host as HTMLElement;

      // Ensure colorValue has proper format - only prepend # if needed
      const formattedColor = colorValue.startsWith("#")
        ? colorValue
        : `#${colorValue}`;
      host.style.setProperty("--company-color", formattedColor);
    }
  };

  // Get effective display names based on props and settings
  const getDisplayNames = (
    propShopName?: string,
    propAffiliateImage?: string,
    propLogoSrc?: string,
  ) => {
    // Shop display name prioritizes prop, then settings, then default
    const shopDisplayName =
      propShopName ?? settings?.settings?.shop?.name ?? "Fluid";

    // Get affiliate name from settings
    const affiliateDisplayName = settings?.settings?.affiliate?.name ?? "";

    // Get logo and avatar sources with fallbacks
    // Priority: 1. Props passed directly, 2. Affiliate data, 3. Shop data
    const logoSrc = propLogoSrc ?? settings?.settings?.shop?.logo_url ?? "";

    // For avatar: 1. Props, 2. Affiliate data, 3. Shop data
    const avatarSrc =
      propAffiliateImage ??
      settings?.settings?.affiliate?.image_url ??
      settings?.settings?.shop?.logo_url ??
      "";

    // Get primary color from settings
    const primaryColor = settings?.settings?.shop?.primary_color;

    return {
      shopDisplayName,
      affiliateDisplayName,
      logoSrc,
      avatarSrc,
      primaryColor,
    };
  };

  return {
    settings,
    applyCompanyColorToShadowDOM,
    getDisplayNames,
  };
}
